<template>
  <!-- One root: the v-if / v-else pair counts as a single root element. -->
  <svg
    v-if="isVector"
    :viewBox="mark.viewBox"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    :aria-label="mark.alt"
  >
    <path :d="mark.path" :fill="fill" />
  </svg>
  <img v-else :src="source" :alt="mark.alt" @error="useFallback" />
</template>

<script>
/**
 * The brand's logo, wherever it appears.
 *
 * Before this component the La Ruota wordmark was copy-pasted into three
 * separate files, which is why the Pizza Garden panel rendered a competitor's
 * wordmark in its sidebar, on its login page and behind its dashboard. The
 * mark now comes from src/brand.js, so it is right on every brand by
 * construction.
 *
 * Two shapes are supported: a vector wordmark, or a bitmap per theme. La Ruota
 * ships its bitmaps inside the bundle, Pizza Garden loads its own from its
 * website so marketing can change it without a panel build. Callers do not need
 * to know which they are getting.
 *
 * Sizing and colour belong to the caller. Pass `fill` for the vector shape;
 * size it with CSS on the parent, the way the three call sites already do.
 */
import { mapState } from "vuex";
import brand from "@/brand";

export default {
  name: "BrandMark",
  props: {
    // Only meaningful for the vector shape. currentColor lets a watermark
    // inherit whatever colour its container sets, which is what the two
    // watermarks rely on.
    fill: { type: String, default: "currentColor" },
    // "dark" or "light": the background the mark sits on, where that does not
    // follow the theme (a brand whose sidebar is black in both themes). Unset,
    // the theme decides.
    surface: { type: String, default: null },
  },
  data() {
    return { remoteFailed: false };
  },
  computed: {
    ...mapState({ stateCommon: "common" }),
    mark() {
      return brand.mark;
    },
    isVector() {
      return this.mark.type === "svg";
    },
    source() {
      const dark = (this.surface || this.stateCommon.theme) === "dark";
      if (!this.remoteFailed) return dark ? this.mark.dark : this.mark.light;
      // A fallback is one file, or one per theme like the remote marks.
      const fallback = this.mark.fallback;
      return typeof fallback === "string" ? fallback : dark ? fallback.dark : fallback.light;
    },
  },
  watch: {
    // A theme switch is a new URL, so give the new one its own chance to load
    // rather than staying on the fallback forever after one bad response.
    // Watch the theme, never `source`: `source` itself changes when the
    // fallback kicks in, so watching it would reset the flag it just set and
    // loop between the two images.
    "stateCommon.theme"() {
      this.remoteFailed = false;
    },
  },
  methods: {
    useFallback() {
      this.remoteFailed = true;
    },
  },
};
</script>
